'use client';

import { useState, useRef, useEffect, useContext, useCallback, useMemo } from "react";
import { Input } from '../ui/input';
import { Search, Bell, Settings, User, LogOut, ChevronDown, MessageSquare, Award, Info, Video, Trophy, Clock, UserPlus, CheckCheck, TrendingUp } from 'lucide-react';
import { Button } from "../ui/button";
import { SidebarTrigger } from '../ui/sidebar';
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";
import { Avatar } from "antd";
import { useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import { API_URL, LOGOUT_URL, NOTIFICATIONS_URL, NOTIFICATIONS_UNREAD_COUNT_URL } from "../../constants/api";
import * as browserNotifs from "../../lib/browserNotifications";
import ToastMsg from "../common/ToastMsg";
import ProjectSwitcher from "../common/ProjectSwitcher";
import { ReportGuideButton } from "../reports/ReportGuide";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const TYPE_ICONS = {
  lead_assigned: <UserPlus className="h-3 w-3" />,
  reassigned: <UserPlus className="h-3 w-3" />,
  status_changed: <TrendingUp className="h-3 w-3" />,
  followup_due: <Clock className="h-3 w-3" />,
  task_assigned: <Info className="h-3 w-3" />,
  task_due: <Clock className="h-3 w-3" />,
};

/**
 * Where a notification should take you.
 *
 * Manager-level alerts land on the Action Center with the matching section open
 * and the lead highlighted — the alert says "a demo was missed", the page
 * answers "how many, whose, and what else is on fire". Per-lead notices
 * (assignment, status change) still go straight to the lead, which is what the
 * recipient of those actually wants.
 */
const ACTION_CENTER_SECTIONS = {
  demo_needs_rescue: 'rescue',
  demo_at_risk: 'at_risk',
  hot_action: 'urgent',
  lead_unowned: 'unowned',
  followup_backlog: 'backlog',
};

/** Always returns { to, state? } so every caller navigates the same way. */
export function alertDestination(item) {
  const section = item && ACTION_CENTER_SECTIONS[item.notification_type];
  if (section) {
    const q = new URLSearchParams({ focus: section });
    if (item.customer_id) q.set('customer', item.customer_id);
    return { to: `/reports/action-center?${q.toString()}` };
  }
  if (item?.customer_id) {
    return {
      to: '/customers/customerdetails',
      state: { formEdit: true, row: { id: item.customer_id } },
    };
  }
  return { to: '/reports/action-center' };
}

export function NotificationDropdown() {
  const { fetch, update, create } = useApiMethods();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifPerm, setNotifPerm] = useState(() => browserNotifs.permission());
  const [browserEnabled, setBrowserEnabled] = useState(() => browserNotifs.isEnabled());
  // Greet the signed-in user by name in the test alert (same source the profile
  // menu below uses). Purely cosmetic — falls back if it isn't set.
  const firstName = useMemo(
    () => (sessionStorage.getItem('user_name') || '').trim().split(/\s+/)[0] || '',
    []
  );
  const dropdownRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch unread count (lightweight, runs every 60s). The same response carries
  // the newest unread rows, so a browser notification costs no extra request.
  const fetchCount = useCallback(async () => {
    try {
      const since = browserNotifs.getLastSeenId();
      const res = await fetch(`${NOTIFICATIONS_UNREAD_COUNT_URL}?since_id=${since}`);
      if (res.status !== 'success') return;
      setUnreadCount(res.unread_count);

      // First poll after a login only records where we are — otherwise the
      // whole existing backlog would toast at once.
      if (since === 0) {
        browserNotifs.setLastSeenId(res.max_id || 0);
        return;
      }
      if (res.new_count > 0 && (res.latest || []).length) {
        browserNotifs.showNotifications(res.latest, res.new_count, (item) => {
          const dest = alertDestination(item);
          navigate(dest.to, dest.state ? { state: dest.state } : undefined);
        });
        browserNotifs.setLastSeenId(res.max_id || res.latest[0].id);
      }
    } catch (_) {}
  }, [fetch, navigate]);

  // Fetch full list when dropdown opens
  const fetchList = useCallback(async () => {
    try {
      const res = await fetch(NOTIFICATIONS_URL);
      if (res.status === 'success') {
        setNotifications(res.data);
        setUnreadCount(res.unread_count);
      }
    } catch (_) {}
  }, [fetch]);

  useEffect(() => {
    fetchCount();
    pollRef.current = setInterval(fetchCount, 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const markAllRead = async () => {
    try {
      await update(NOTIFICATIONS_URL, {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const handleNotifClick = async (n) => {
    if (!n.is_read) {
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
      try { await create(NOTIFICATIONS_URL, { ids: [n.id] }); } catch (_) {}
    }
    // Same routing as the desktop toast: manager alerts open the Action Center
    // with the relevant section expanded; per-lead notices open the lead.
    setOpen(false);
    const dest = alertDestination(n);
    navigate(dest.to, dest.state ? { state: dest.state } : undefined);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        onClick={() => setOpen(prev => !prev)}
        variant="ghost"
        size="icon"
        className={cn(
          "relative rounded-full h-9 w-9",
          "text-muted-foreground hover:text-foreground bg-transparent hover:bg-accent transition-colors duration-200",
          open && "bg-accent text-foreground"
        )}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-[9px] font-bold items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          </span>
        )}
      </Button>

      {open && (
        <div className={cn(
          "absolute right-0 mt-2 w-80 sm:w-96 rounded-lg border bg-popover text-popover-foreground",
          "shadow-lg z-50 overflow-hidden animate-in fade-in-80 slide-in-from-top-2"
        )}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-950/60 dark:to-brand-900/40">
            <div>
              <h3 className="text-sm font-semibold">Notifications</h3>
              <p className="text-xs text-muted-foreground">{unreadCount} unread</p>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-primary hover:underline"
              >
                <CheckCheck className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>

          {/* Desktop alerts opt-in. Permission must be asked from a click, so it
              lives here rather than firing on page load. */}
          {browserNotifs.isSupported() && (
            <div className="px-4 py-2 border-b bg-muted/40 flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {notifPerm === "granted"
                  ? (browserEnabled
                      ? "Desktop alerts on — shown when this tab isn't in focus"
                      : "Desktop alerts paused")
                  : notifPerm === "denied"
                    ? "Desktop alerts blocked in browser settings"
                    : "Get a desktop alert for urgent leads"}
              </span>
              {notifPerm === "denied" ? (
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                  unblock in site settings
                </span>
              ) : notifPerm === "granted" ? (
                <div className="flex items-center gap-3 whitespace-nowrap">
                  {/* Fires in this browser only — nothing is saved or sent */}
                  <button
                    onClick={() => {
                      const result = browserNotifs.showTestNotification(firstName);
                      if (result !== "sent") ToastMsg("error", "Could not show a test alert");
                    }}
                    className="text-xs text-muted-foreground hover:text-primary hover:underline"
                  >
                    Send test
                  </button>
                  <button
                    onClick={() => {
                      const next = !browserEnabled;
                      browserNotifs.setEnabled(next);
                      setBrowserEnabled(next);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    {browserEnabled ? "Turn off" : "Turn on"}
                  </button>
                </div>
              ) : (
                <button
                  onClick={async () => {
                    const result = await browserNotifs.requestPermission();
                    setNotifPerm(result);
                    setBrowserEnabled(browserNotifs.isEnabled());
                  }}
                  className="text-xs text-primary hover:underline whitespace-nowrap"
                >
                  Enable
                </button>
              )}
            </div>
          )}

          {/* List */}
          <ul className="max-h-80 overflow-y-auto divide-y divide-border">
            {notifications.map(n => (
              <li
                key={n.id}
                onClick={() => handleNotifClick(n)}
                className={cn(
                  "px-4 py-3 cursor-pointer transition-colors hover:bg-accent/40 border-l-4",
                  !n.is_read ? "bg-accent/20 border-l-primary" : "border-l-transparent"
                )}
              >
                <div className="flex gap-3 items-start">
                  <div className={cn(
                    "flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs",
                    !n.is_read ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  )}>
                    {TYPE_ICONS[n.type] || <Bell className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn("text-sm font-medium truncate", !n.is_read ? "text-foreground" : "text-muted-foreground")}>
                      {n.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {n.created_date ? dayjs(n.created_date).fromNow() : ''}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {notifications.length === 0 && (
            <div className="px-4 py-8 text-center">
              <Bell className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">You're all caught up!</p>
            </div>
          )}

          <div className="px-4 py-2 border-t bg-muted/30 text-center">
            <button
              className="text-xs text-primary hover:underline"
              onClick={() => { setOpen(false); navigate('/notifications'); }}
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


export function UserDropdown() {
  const { logoutauth } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const employee_name = sessionStorage.getItem('user_name')
  const profile = sessionStorage.getItem('profile')
  const department = sessionStorage.getItem('department')

  // `profile` is NOT an image URL. The login payload puts a name object there
  // ({login_fname, login_lname, login_email_address, login_phone_number}) and
  // AuthContext JSON-stringifies it into sessionStorage. Feeding that to
  // <Avatar src> produced a broken image, which is why only the gradient ring
  // showed. Parse it for a display name, and keep the image branch for the day
  // the API does start returning a real avatar URL.
  const profileData = useMemo(() => {
    if (!profile) return null;
    try { return JSON.parse(profile); } catch { return null; }
  }, [profile]);

  const avatarImage = /^(https?:|data:|\/)/.test(profile || '') ? profile : null;

  const avatarLetter = (
    (profileData?.login_fname || employee_name || '').trim()[0] || 'U'
  ).toUpperCase();
  const designation = sessionStorage.getItem('designation')
  const companyName = sessionStorage.getItem('companyName')

  const navigate = useNavigate();

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const menuItems = [
    {
      icon: User,
      label: "My Profile",
      onClick: () => navigate("/employeeprofile"),
      description: "View and edit your profile"
    },
    {
      icon: Settings,
      label: "Settings",
      onClick: () => { /* Add settings navigation */ },
      description: "Customize your preferences"
    },
  ];

  const handleLogout = () => {
    logoutauth();
    window.location.href = '/';
  };

  return (
    <div className="relative font-sans" ref={menuRef}>
      {/* User Button */}
      <Button
        onClick={() => setOpen(!open)}
        variant="ghost"
        className={cn(
          "h-11 px-2 gap-2",
          open ? "bg-accent" :
            "bg-transparent hover:bg-accent",
          "text-foreground hover:text-foreground",
          "transition-colors duration-200",
          "border border-transparent hover:border-border"
        )}
      >
        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[var(--brand-from)] to-[var(--brand-to)] flex items-center justify-center text-white text-xs font-semibold overflow-hidden select-none leading-none">
          {avatarImage
            ? <Avatar src={avatarImage} className="w-full h-full object-cover" />
            : avatarLetter}
        </div>
        {/* <div className="hidden sm:flex flex-col items-start">
          <span className="text-sm font-medium">{employee_name}</span>
          <span className="text-xs text-muted-foreground">{department}{" "}|{" "}{designation}</span>
        </div> */}
        <ChevronDown className={cn(
          "h-4 w-4 text-muted-foreground transition-transform",
          open && "rotate-180"
        )} />
      </Button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className={cn(
            "absolute right-0 top-12 w-72 rounded-xl border shadow-lg z-50 overflow-hidden backdrop-blur-sm animate-in fade-in-80 slide-in-from-top-2 font-sans",

            // Light mode base
            "bg-white text-gray-800 border-gray-200",

            // Dark mode overrides
            "dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
          )}
        >
          {/* User Info Header */}
          <div className="px-5 py-4 border-b 
      bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-950/40 dark:to-brand-900/30 
      border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[var(--brand-from)] to-[var(--brand-to)] flex items-center justify-center overflow-hidden select-none leading-none text-white text-base font-semibold">
                {avatarImage
                  ? <Avatar src={avatarImage} size="large" className="cursor-pointer scale-125 hover:shadow-lg" />
                  : avatarLetter}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate capitalize">{employee_name || 'User'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{department || 'Sales'}{" "}|{" "}{designation || 'Strategy Analyst'}</p>
              </div>
            </div>
            <p className="text-xs text-primary font-medium truncate">
              {companyName}
            </p>
          </div>

          {/* Menu Items */}
          {/* <div className="py-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className={cn(
                  "w-full flex items-start gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 border-l-transparent font-sans",

                  // Light mode hover
                  "hover:bg-accent hover:text-accent-foreground hover:border-l-primary",

                  // Dark mode hover
                  "dark:hover:bg-accent dark:hover:text-accent-foreground dark:hover:border-l-primary"
                )}
              >
                <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 
            group-hover:bg-brand-100 group-hover:text-primary
            dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-brand-900/40 dark:group-hover:text-brand-300"
                >
                  <item.icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                    {item.description}
                  </p>
                </div>
              </button>
            ))}
          </div> */}

          {/* Logout Section */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 border-l-transparent",

                // Light mode
                "text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-l-red-500",

                // Dark mode
                "dark:text-red-400 dark:hover:bg-red-900/40 dark:hover:text-red-300 dark:hover:border-l-red-500"
              )}
            >
              <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md 
          bg-red-100 text-red-600 group-hover:text-red-700
          dark:bg-red-900/30 dark:text-red-400 dark:group-hover:text-red-300"
              >
                <LogOut className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Log out</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteHeader() {
  return (
    <header className={cn(
      "sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      "transition-colors duration-200"
    )}>
      <div className="flex h-12 items-center justify-between px-4 sm:px-6">
        {/* Sidebar Trigger — visible on all screen sizes */}
        <div className="flex items-center">
          <SidebarTrigger />
        </div>

        {/* Project Switcher */}
        <div className="hidden md:flex items-center ml-4">
          <ProjectSwitcher />
        </div>

        {/* Search Bar - Hidden on mobile, visible on desktop */}
        <div className="relative hidden md:block flex-1 max-w-sm lg:max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search..."
              className={cn(
                "pl-9 pr-4 h-9 w-full rounded-lg",
                "bg-muted/50 border-border",
                "placeholder:text-muted-foreground",
                "transition-colors duration-200",
                "shadow-sm"
              )}
            />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          <ReportGuideButton />
          <NotificationDropdown />
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}